import React, {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  CustomTooltipTracker,
  TrendChartSummarySection,
  TrendChartZoneLabelBands,
} from "./index";
import { useChartSelection, useTrendChartCompactViewport } from "../hooks";
import {
  getChartFocusStyles,
  getTrendChartLayoutZoneLabels,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../utils";
import { ChartDataPoint } from "../types";

export type ExerciseChartDataPoint = ChartDataPoint & {
  minutesPerWeek: number;
  valueDisplayLabel?: string;
};

export type SleepChartDataPoint = ChartDataPoint & {
  sleepHours: number;
  valueDisplayLabel?: string;
};

export const getExerciseColor = (minutesPerWeek: number): string => {
  if (minutesPerWeek >= 300) {
    return themeColors.success;
  }
  if (minutesPerWeek >= 150) {
    return themeColors.warning;
  }
  return themeColors.error;
};

export const getSleepColor = (sleepHours: number): string => {
  if (sleepHours > 9) {
    return themeColors.success;
  }
  if (sleepHours >= 7) {
    return themeColors.warning;
  }
  return themeColors.error;
};

const CHART_HEIGHT = 200;
const Y_MARGIN = { top: 10, bottom: 10 };
const EXERCISE_Y_TICKS = [150, 300];
const SLEEP_Y_TICKS = [7, 9];

import {
  getExerciseDisplayValue,
  getSleepDisplayValue,
  formatYAxisTick,
  computeYDomain,
  buildBandCenters,
} from "../utils/lifestyleChartHelpers";

function ExerciseReferenceZones() {
  return (
    <>
      <ReferenceArea
        y1={300}
        y2={400}
        fill={backgroundColors.successLight}
        fillOpacity={0.5}
      />
      <ReferenceArea
        y1={150}
        y2={300}
        fill={backgroundColors.warningLight}
        fillOpacity={0.5}
      />
      <ReferenceArea
        y1={0}
        y2={150}
        fill={backgroundColors.errorLight}
        fillOpacity={0.5}
      />
      <ReferenceLine
        y={300}
        stroke={themeColors.success}
        strokeDasharray="3 3"
        strokeWidth={1}
      />
      <ReferenceLine
        y={150}
        stroke={themeColors.warning}
        strokeDasharray="3 3"
        strokeWidth={1}
      />
    </>
  );
}

function SleepReferenceZones() {
  return (
    <>
      <ReferenceArea
        y1={9}
        y2={12}
        fill={backgroundColors.successLight}
        fillOpacity={0.5}
      />
      <ReferenceArea
        y1={7}
        y2={9}
        fill={backgroundColors.warningLight}
        fillOpacity={0.5}
      />
      <ReferenceArea
        y1={0}
        y2={7}
        fill={backgroundColors.errorLight}
        fillOpacity={0.5}
      />
      <ReferenceLine
        y={9}
        stroke={themeColors.success}
        strokeDasharray="3 3"
        strokeWidth={1}
      />
      <ReferenceLine
        y={7}
        stroke={themeColors.warning}
        strokeDasharray="3 3"
        strokeWidth={1}
      />
    </>
  );
}

function ExerciseLineDot(dotProps: any) {
  return <CustomDot {...dotProps} getColorFn={getExerciseColor} />;
}

function SleepLineDot(dotProps: any) {
  return <CustomDot {...dotProps} getColorFn={getSleepColor} />;
}

function ExerciseLineActiveDot(dotProps: any) {
  return <CustomActiveDot {...dotProps} getColorFn={getExerciseColor} />;
}

function SleepLineActiveDot(dotProps: any) {
  return <CustomActiveDot {...dotProps} getColorFn={getSleepColor} />;
}

type LifestyleHoveredPoint =
  | ExerciseChartDataPoint
  | SleepChartDataPoint
  | null;

function renderLifestyleTooltip(
  setHoveredPoint: Dispatch<SetStateAction<LifestyleHoveredPoint>>,
  tooltipProps: any
) {
  return (
    <CustomTooltipTracker
      {...tooltipProps}
      setHoveredPoint={setHoveredPoint as (point: any) => void}
    />
  );
}

type LifestyleTrendValueDisplayProps = {
  displayValue: string;
  unitLabel: string;
};

function LifestyleTrendValueDisplay({
  displayValue,
  unitLabel,
}: LifestyleTrendValueDisplayProps) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <span
        style={{
          fontSize: convertPxToRem(24),
          fontWeight: 800,
          color: theme.palette.grey[700],
        }}
      >
        {displayValue}
      </span>
      <span
        style={{
          fontSize: convertPxToRem(14),
          color: theme.palette.grey[900],
          marginLeft: convertPxToRem(4),
        }}
      >
        {unitLabel}
      </span>
    </div>
  );
}

export type LifestyleThreeZoneTrendChartProps =
  | { variant: "exercise"; data: ExerciseChartDataPoint[] }
  | { variant: "sleep"; data: SleepChartDataPoint[] };

const LifestyleThreeZoneTrendChart: React.FC<
  LifestyleThreeZoneTrendChartProps
> = (props) => {
  const { variant, data } = props;
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const yAxisWidthFull = variant === "exercise" ? 30 : 27;
  const {
    left: chartLeftMargin,
    right: chartRightMargin,
    yAxisWidth,
  } = getTrendChartLayoutZoneLabels(isCompactChart, yAxisWidthFull);

  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection<ExerciseChartDataPoint | SleepChartDataPoint>(
    data as (ExerciseChartDataPoint | SleepChartDataPoint)[]
  );

  const yDomain = useMemo(() => computeYDomain(variant, data), [data, variant]);

  const yTicks = variant === "exercise" ? EXERCISE_Y_TICKS : SLEEP_Y_TICKS;
  const chartClassName =
    variant === "exercise" ? "exercise-chart" : "sleep-chart";
  const dataKey = variant === "exercise" ? "minutesPerWeek" : "sleepHours";

  const titleKey =
    variant === "exercise"
      ? "HealthChart.exerciseChart.title"
      : "HealthChart.sleepChart.title";
  const datePrefixKey =
    variant === "exercise"
      ? "HealthChart.exerciseChart.datePrefix"
      : "HealthChart.sleepChart.datePrefix";
  const unitKey =
    variant === "exercise"
      ? "HealthChart.exerciseChart.unit"
      : "HealthChart.sleepChart.unit";

  const bandCenters = useMemo(() => buildBandCenters(variant, t), [variant, t]);

  const tickFormatter = useCallback(
    (value: number) => formatYAxisTick(value, variant),
    [variant]
  );

  const tooltipContent = useCallback(
    (tooltipProps: any) =>
      renderLifestyleTooltip(setHoveredPoint, tooltipProps),
    [setHoveredPoint]
  );

  if (!activePoint) {
    return null;
  }

  const displayValue =
    variant === "exercise"
      ? getExerciseDisplayValue(activePoint as ExerciseChartDataPoint, t)
      : getSleepDisplayValue(activePoint as SleepChartDataPoint, t);

  const lineDot = variant === "exercise" ? ExerciseLineDot : SleepLineDot;
  const lineActiveDot =
    variant === "exercise" ? ExerciseLineActiveDot : SleepLineActiveDot;

  return (
    <>
      <style>{getChartFocusStyles(chartClassName)}</style>
      <Box className={chartClassName} sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          <Typography
            variant="h4"
            sx={{
              fontSize: 16,
            }}
          >
            {t(titleKey)}
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: CHART_HEIGHT,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data as ChartDataPoint[]}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: chartLeftMargin,
                  bottom: 10,
                }}
                onClick={handlePointClick}
              >
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal={true}
                />

                {variant === "exercise" ? (
                  <ExerciseReferenceZones />
                ) : (
                  <SleepReferenceZones />
                )}

                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={yTicks}
                  tickFormatter={tickFormatter}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={yAxisWidth}
                />

                <Tooltip
                  content={tooltipContent}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                <Line
                  type="linear"
                  dataKey={dataKey}
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={lineDot}
                  activeDot={lineActiveDot}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>

            <TrendChartZoneLabelBands
              chartRightMargin={chartRightMargin}
              chartHeight={CHART_HEIGHT}
              yMargin={Y_MARGIN}
              yDomain={yDomain}
              bandCenters={bandCenters}
            />
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={chartLeftMargin}
            chevronRightMargin={chartRightMargin}
            datePrefix={t(datePrefixKey)}
            fullDate={activePoint.fullDate}
          >
            <LifestyleTrendValueDisplay
              displayValue={displayValue}
              unitLabel={t(unitKey)}
            />
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default LifestyleThreeZoneTrendChart;
